import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import router from '@/router'
import { usePermissionStore } from '@/stores/permissions'
import { useUiStore } from '@/stores/ui'
import { captureApiException, addBreadcrumb } from '@/core/sentry'

/**
 * Axios instance configured for the RWS API
 * Automatically attaches JWT token for authentication
 */
const api: AxiosInstance = axios.create({
    baseURL: import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'),
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
    paramsSerializer: {
        serialize: (params) => {
            const parts: string[] = []
            for (const [key, value] of Object.entries(params)) {
                if (value === undefined || value === null) continue
                if (Array.isArray(value)) {
                    parts.push(`${encodeURIComponent(key)}=${value.map(encodeURIComponent).join(',')}`)
                } else {
                    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                }
            }
            return parts.join('&')
        },
    },
})

// Storage key constants
export const AUTH_TOKEN_KEY = 'rws_access_token'

// Getters - will be set by auth store
let getAccessToken: (() => string | null) | null = null
let getOrganizationId: (() => string | undefined) | null = null
let organizationIdOverride: string | null = null
let refreshFunction: (() => Promise<string | null>) | null = null

/**
 * Set the token getter function (called from auth store)
 * For local auth, this reads from local storage synchronously.
 */
export function setTokenGetter(getter: () => string | null) {
    getAccessToken = getter
}

/**
 * Set the organization ID getter function (called from auth store)
 */
export function setOrganizationIdGetter(getter: () => string | undefined) {
    getOrganizationId = getter
}

/**
 * Override the organization ID for public/embed views.
 * Takes priority over the auth store getter.
 */
export function setOrganizationIdOverride(orgId: string | null) {
    organizationIdOverride = orgId
}

/**
 * Set the function to call when a 401 occurs to refresh the token
 */
export function setRefreshFunction(fn: () => Promise<string | null>) {
    refreshFunction = fn
}

// Request interceptor to attach JWT
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        try {
            // Attach JWT Token (if available)
            // Primary: use getter registered by auth store; fallback: read localStorage directly
            // (the getter may be null during Vite HMR before the store re-registers)
            let token: string | null = null
            if (getAccessToken) {
                token = getAccessToken()
            }
            if (!token) {
                token = localStorage.getItem(AUTH_TOKEN_KEY)
            }
            if (token) {
                if (config.headers && typeof config.headers.set === 'function') {
                    config.headers.set('Authorization', `Bearer ${token}`)
                } else {
                    config.headers.Authorization = `Bearer ${token}`
                }
            }

            // Attach Organization ID (override takes priority)
            const organizationId = organizationIdOverride || (getOrganizationId ? getOrganizationId() : undefined)
            if (organizationId) {
                // Check if path is NOT global (no organization ID needed for global endpoints)
                const isGlobalPath = config.url?.startsWith('/auth') ||
                    config.url?.startsWith('/organizations') ||
                    config.url?.startsWith('/health') ||
                    config.url?.startsWith('/enrichment') ||
                    config.url?.startsWith('/o365')

                if (!isGlobalPath) {
                    if (config.headers && typeof config.headers.set === 'function') {
                        config.headers.set('X-Organization-ID', organizationId)
                    } else if (config.headers) {
                        config.headers['X-Organization-ID'] = organizationId
                    }
                }
            }
        } catch (error) {
            console.error('Failed to get access token:', error)
        }

        return config
    },
    (error) => Promise.reject(error)
)

// Response interceptor for error handling and automatic token refresh
let isRefreshing = false
let failedQueue: any[] = []

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve(token)
        }
    })

    failedQueue = []
}

api.interceptors.response.use(
    (response) => {
        // Record breadcrumb for successful API calls
        if (response?.config) {
            addBreadcrumb({
                category: 'api',
                message: `${response.config.method?.toUpperCase() || 'GET'} ${response.config.url || ''} [${response.status || ''}]`,
                level: 'info',
            })
        }
        return response
    },
    async (error) => {
        const originalRequest = error.config
        const status = error.response?.status

        // Check if request was intentionally aborted / cancelled (e.g. search debounce or component unmount)
        const isCancelled = axios.isCancel(error) || error?.code === 'ERR_CANCELED'
        if (isCancelled) {
            addBreadcrumb({
                category: 'api',
                message: `${originalRequest?.method?.toUpperCase() || 'GET'} ${originalRequest?.url || 'unknown'} [CANCELLED]`,
                level: 'info',
            })
            return Promise.reject(error)
        }

        // Breadcrumb for failed request
        addBreadcrumb({
            category: 'api',
            message: `${originalRequest?.method?.toUpperCase() || 'GET'} ${originalRequest?.url || 'unknown'} [${status ?? 'ERR'}]`,
            level: (status && status < 500) ? 'warning' : 'error',
            data: {
                status,
                statusText: error.response?.statusText,
                message: error.message,
            },
        })

        // Capture 400 Bad Request / 422 Unprocessable Entity (client schema/call bug)
        // or 5xx Server Error or Network dropped error to Sentry
        const isClientApiError = status === 400 || status === 422
        const isServerError = typeof status === 'number' && status >= 500
        const isNetworkFailure = !error.response && !!error.request

        if (isClientApiError || isServerError || isNetworkFailure) {
            captureApiException(error, {
                endpoint: originalRequest?.url,
                method: originalRequest?.method,
                status,
                data: error.response?.data,
            })
        }

        // If error is 401 and we haven't retried yet — attempt silent token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
            // Auth endpoints handle their own 401s (e.g. bad credentials)
            if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/magic-link/callback')) {
                return Promise.reject(error)
            }

            // Another request is already refreshing — queue this one
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                })
                    .then((token) => {
                        if (originalRequest.headers && typeof originalRequest.headers.set === 'function') {
                            originalRequest.headers.set('Authorization', `Bearer ${token}`)
                        } else if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`
                        }
                        originalRequest._retry = true
                        return api.request({ ...originalRequest })
                    })
                    .catch((err) => Promise.reject(err))
            }

            originalRequest._retry = true
            isRefreshing = true

            if (refreshFunction) {
                try {
                    console.log('Token expired, attempting silent refresh…')
                    const newToken = await refreshFunction()

                    if (newToken) {
                        processQueue(null, newToken)
                        if (originalRequest.headers && typeof originalRequest.headers.set === 'function') {
                            originalRequest.headers.set('Authorization', `Bearer ${newToken}`)
                        } else if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${newToken}`
                        }
                        return api.request({ ...originalRequest })
                    }

                    // Refresh returned null — no refresh token or it failed.
                    // Auth store already called clearAuth(), now we redirect.
                    processQueue(new Error('Session expired'), null)
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError)
                    processQueue(refreshError, null)
                } finally {
                    isRefreshing = false
                }
            } else {
                isRefreshing = false
                processQueue(new Error('No refresh function set'), null)
            }

            // Token refresh failed — hard redirect to login.
            // window.location.href does a full page reload, which cleanly
            // destroys all Vue components. router.push() causes Vue crashes
            // because it tears down components that still have pending async ops.
            // The never-resolving promise prevents callers from rendering errors
            // during the brief window before the browser navigates away.
            console.warn('Session expired — redirecting to login')
            window.location.href = '/login'
            return new Promise(() => { })
        }

        // Global handling for authorization & not-found errors
        // Skip if request was already retried (refresh succeeded, new error is different)
        if (error.response && !originalRequest._retry) {
            const status = error.response.status
            // Don't redirect auth-related pages — they handle their own errors
            if (!originalRequest.url?.includes('/auth/')) {
                if (status === 403) {
                    // Permission denied — re-fetch permissions (may be stale) and show toast
                    const permissionStore = usePermissionStore()
                    const uiStore = useUiStore()
                    permissionStore.load()
                    uiStore.error('You do not have permission to perform this action')
                } else if (status === 404 && !originalRequest._skipNotFound) {
                    router.push({ name: 'NotFound' })
                }
            }
        }

        return Promise.reject(error)
    }
)

export default api
